import React, { useEffect, useMemo, useState } from 'react';
import {
  Table,
  Button,
  Space,
  Input,
  Switch,
  Tag,
  Typography,
  InputNumber,
  message,
  Select,
} from 'antd';
import { PlusOutlined, SaveOutlined, EditOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import * as Icons from '@ant-design/icons';
import {
  listActivityTypes,
  createActivityType,
  updateActivityType,
  type ActivityTypeDTO,
} from '../../services/activityTypes';
import { renderAntdIcon } from '../../utils/antdIcon';

const { Text } = Typography;

type Row = ActivityTypeDTO & { _tmpId?: number; _isNew?: boolean };

const isProtected = (code?: string | null) =>
  (code || '').trim().toUpperCase() === 'EXTRA' ||
  (code || '').trim().toUpperCase() === 'MEDIA_SHOOT';

// number|string|null|undefined -> number|null
const normalizeNum = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};
// per InputNumber serve undefined quando vuoto
const toInputValue = (v: unknown): number | undefined => {
  const n = normalizeNum(v);
  return n === null ? undefined : n;
};

const ActivityTypeManager: React.FC = () => {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingKey, setEditingKey] = useState<number | string | null>(null);

  // Solo icone *Outlined
  const iconOptions = useMemo(() => {
    const names = Object.keys(Icons)
      .filter((k) => k.endsWith('Outlined'))
      .sort((a, b) => a.localeCompare(b, 'it', { sensitivity: 'base' }));
    return names.map((name) => ({
      label: (
        <Space size={8}>
          <span style={{ fontSize: 18 }}>{renderAntdIcon(name)}</span>
          <span>{name}</span>
        </Space>
      ),
      value: name,
    }));
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const list = await listActivityTypes();
      setRows((list || []).map((r) => ({ ...r })));
    } catch (e: any) {
      message.error(e?.message || 'Errore caricamento tipi attività');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchData(); }, []);

  const isEditing = (r: Row) => (r.id ?? r._tmpId) === editingKey;
  const updateRow = (key: number | string | undefined, patch: Partial<Row>) =>
    setRows((prev) => prev.map((r) => ((r.id ?? r._tmpId) === key ? { ...r, ...patch } : r)));

  const addRow = () => {
    const tmp: Row = {
      _tmpId: Date.now() * -1,
      _isNew: true,
      id: undefined as any,
      code: '',
      label: '',
      icon: '',
      active: true,
      defaultDurationMinutes: null,
      defaultCost: null,
    };
    setRows((prev) => [tmp, ...prev]);
    setEditingKey(tmp._tmpId!);
  };

  const startEdit = (r: Row) => setEditingKey(r.id ?? r._tmpId ?? '');
  const cancelEdit = () => setEditingKey(null);

  const save = async (r: Row) => {
    const key = r.id ?? r._tmpId;
    const code = (r.code || '').trim().toUpperCase();
    const label = (r.label || '').trim();
    if (!code) return message.warning('Inserisci il codice');
    if (!label) return message.warning('Inserisci l’etichetta');

    const payload: ActivityTypeDTO = {
      id: (r.id as number) ?? 0,
      code,
      label,
      icon: r.icon || null,
      active: isProtected(code) ? true : !!r.active,
      defaultDurationMinutes: normalizeNum(r.defaultDurationMinutes),
      defaultCost: normalizeNum(r.defaultCost),
    };

    try {
      setLoading(true);
      if (r._isNew || !r.id) {
        const created = await createActivityType(payload);
        setRows((prev) => prev.map((x) => ((x.id ?? x._tmpId) === key ? { ...created } : x)));
        message.success('Tipologia creata');
      } else {
        const updated = await updateActivityType(r.id as number, payload);
        setRows((prev) => prev.map((x) => ((x.id ?? x._tmpId) === key ? { ...updated } : x)));
        message.success('Tipologia aggiornata');
      }
      setEditingKey(null);
    } catch (e: any) {
      message.error(e?.message || 'Errore salvataggio');
    } finally {
      setLoading(false);
    }
  };

  const dupCodes = useMemo(() => {
    const m = new Map<string, number>();
    rows.forEach((r) => {
      const c = (r.code || '').trim().toUpperCase();
      if (!c) return;
      m.set(c, (m.get(c) || 0) + 1);
    });
    const entries = Array.from(m.entries());
    return entries.filter(([, n]) => n > 1).map(([c]) => c);
  }, [rows]);

  const columns: ColumnsType<Row> = [
    {
      title: 'Icona',
      dataIndex: 'icon',
      width: 260,
      render: (_: any, r) =>
        isEditing(r) ? (
          <Select
            showSearch
            allowClear
            placeholder="Seleziona icona"
            style={{ width: '100%' }}
            value={r.icon || undefined}
            onChange={(val) => updateRow(r.id ?? r._tmpId, { icon: val || '' })}
            filterOption={(input, option) =>
              (option?.value as string)?.toLowerCase().includes(input.toLowerCase())
            }
            options={iconOptions}
            optionLabelProp="value"
            dropdownMatchSelectWidth={480}
          />
        ) : (
          <Space>
            <span style={{ fontSize: 18 }}>{renderAntdIcon(r.icon)}</span>
            <Text type={r.icon ? undefined : 'secondary'}>{r.icon || '—'}</Text>
          </Space>
        ),
    },
    {
      title: 'Codice',
      dataIndex: 'code',
      width: 180,
      render: (v: string, r) =>
        isEditing(r) ? (
          <Input
            size="small"
            value={v}
            onChange={(e) => updateRow(r.id ?? r._tmpId, { code: e.target.value.toUpperCase() })}
            placeholder="SOC_IG_POST"
          />
        ) : (
          <Tag>{v}</Tag>
        ),
    },
    {
      title: 'Etichetta',
      dataIndex: 'label',
      render: (v: string, r) =>
        isEditing(r) ? (
          <Input
            size="small"
            value={v}
            onChange={(e) => updateRow(r.id ?? r._tmpId, { label: e.target.value })}
            placeholder="Post Instagram"
          />
        ) : (
          <Text>{v}</Text>
        ),
    },
    {
      title: 'Durata default (min)',
      dataIndex: 'defaultDurationMinutes',
      width: 160,
      align: 'center',
      render: (v: any, r) =>
        isEditing(r) ? (
          <InputNumber
            size="small"
            min={0}
            step={5}
            style={{ width: '100%' }}
            value={toInputValue(v)}
            onChange={(val) =>
              updateRow(r.id ?? r._tmpId, { defaultDurationMinutes: (val ?? null) as number | null })
            }
          />
        ) : (
          <Text>{v ?? '—'}</Text>
        ),
    },
    {
      title: 'Costo default (€)',
      dataIndex: 'defaultCost',
      width: 160,
      align: 'center',
      render: (v: any, r) =>
        isEditing(r) ? (
          <InputNumber
            size="small"
            min={0}
            step={1}
            style={{ width: '100%' }}
            value={toInputValue(v)}
            onChange={(val) =>
              updateRow(r.id ?? r._tmpId, { defaultCost: (val ?? null) as number | null })
            }
          />
        ) : (
          <Text>{v ?? '—'}</Text>
        ),
    },
    {
      title: 'Attivo',
      dataIndex: 'active',
      width: 110,
      align: 'center',
      render: (v: boolean, r) =>
        isEditing(r) ? (
          <Switch
            checked={!!v}
            onChange={(checked) =>
              updateRow(r.id ?? r._tmpId, { active: isProtected(r.code) ? true : checked })
            }
            disabled={isProtected(r.code)}
          />
        ) : (
          <Switch checked={!!v} disabled />
        ),
    },
    {
      key: 'actions',
      width: 140,
      align: 'right',
      render: (_: any, r) =>
        isEditing(r) ? (
          <Space>
            <Button
              type="primary"
              size="small"
              icon={<SaveOutlined />}
              onClick={() => save(r)}
            />
            <Button size="small" onClick={cancelEdit}>Annulla</Button>
          </Space>
        ) : (
          <Button size="small" icon={<EditOutlined />} onClick={() => startEdit(r)} />
        ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 12 }}>
        <Button icon={<PlusOutlined />} onClick={addRow}>Aggiungi Tipologia</Button>
        <Button icon={<ReloadOutlined />} onClick={fetchData}>Ricarica</Button>
        {dupCodes.length > 0 && (
          <Text type="danger">Codici duplicati: {dupCodes.join(', ')}</Text>
        )}
      </Space>

      <Table<Row>
        size="small"
        rowKey={(r) => (r.id ?? r._tmpId)!}
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={false}
        scroll={{ x: 980 }}
      />
    </div>
  );
};

export default ActivityTypeManager;
